import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { ActivatedRoute } from '@angular/router';
import { GoogleAnalyticsService } from '@app/services/_index';
import { HasAnalytics } from '@app/interfaces/_index';
import { SessionStorageKeys } from '@app/enums/_index';
import { StaticPage } from '@app/models/_index';
import { Subscription } from 'rxjs';
import { logGoogleAnalytics } from '@app/utilities/_index';

@Component({
  selector: 'app-aside-list-navigation',
  templateUrl: './aside-list-navigation.component.html',
  styleUrls: ['./aside-list-navigation.component.scss'],
  standalone: false,
})
export class AsideListNavigationComponent implements OnInit, OnDestroy, HasAnalytics {
  @Input() navItems: StaticPage[]; // Parent navigation item
  @Input() childNavItems: StaticPage[]; // Optional navigation item, if they share the same parent in the navItems
  @Input() slugname: string;
  @Input() toggleExpandCollapse = ''; // Expand Collapse Instruction List Accordion

  slug: string;
  subs: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private googleAnalyticsService: GoogleAnalyticsService
  ) {}

  /**
   * On Init, load and subscribe to route params to set the slug and activate navigate items
   */
  ngOnInit(): void {
    this.subs.push(
      this.route.data.subscribe((value) => {
        this.slug = value.slug;
      })
    );
  }

  /**
   * Send the query search label to Google Analytics
   * @param category - the source of the google analytics category string (ex. the page or component triggering the action)
   * @param action - the action to be (ex. click)
   * @param dataLabel - the label associated with the action
   */
  sendGoogleAnalytics(category: string, action: string, dataLabel: string): void {
    logGoogleAnalytics(category, action, dataLabel);
    this.googleAnalyticsService.sendEvent(category, action, dataLabel);
  }

  /**
   * Add Track By Navigation Item Id to not redraw or re-render list items that have not changed.
   * This improves performance.
   * @param index - Index of Navigation array
   * @param navigationItem - Static Page navigation item
   * @returns navigation Item id
   */
  trackByNavigationItemId(index: number, navigationItem: StaticPage): number {
    if (navigationItem) {
      return navigationItem.id;
    } else {
      return index;
    }
  }

  /**
   * Toggle accordion to expand or collapse
   * @param index - selected index isExpanded to determine what part of the accordion to expand or collapse
   * @param parentSlug - parent slug
   */
  toggleAccordion(index: number, parentSlug: string): void {
    if (this.navItems && this.navItems.length > 0) {
      this.navItems[index].isExpanded = !this.navItems[index].isExpanded;
      sessionStorage.setItem(
        `${String(SessionStorageKeys.ACCORDION)}${parentSlug}-${index}`,
        String(this.navItems[index].isExpanded)
      );
    }
  }

  /**
   * Check Accordion State and returns boolean true or false
   * @param parentIndex - parent index
   * @param parentSlug - parent slug
   * @returns boolean variable true or false
   */
  checkAccordion(parentIndex: number, parentSlug: string): boolean {
    if (parentIndex) {
      const isParentIndexValid = sessionStorage.getItem(
        `${String(SessionStorageKeys.ACCORDION)}${parentSlug}-${parentIndex}`
      );
      if (isParentIndexValid == 'true') {
        return (this.navItems[parentIndex].isExpanded = !!isParentIndexValid);
      } else {
        return (this.navItems[parentIndex].isExpanded = false);
      }
    }
  }

  /**
   * On Destroy, unsubscribe subscriptions to prevent memory leaks
   */
  ngOnDestroy(): void {
    if (this.subs) {
      this.subs.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }
}
